import { useState, useEffect, useCallback } from 'react';
import { adminService } from '../services/adminService';

export const useCustomers = () => {
    const [customers, setCustomers] = useState([]);
    const [filteredCustomers, setFilteredCustomers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    // Filters
    const [searchQuery, setSearchQuery] = useState('');
    const [statusFilter, setStatusFilter] = useState('all');
    const [provisionFilter, setProvisionFilter] = useState('all'); // B2B provision filter (all|none|pending|active|inactive|rejected)
    const [sortConfig, setSortConfig] = useState({ key: 'createdAt', direction: 'desc' });

    // Fetch Customers — no mock fallback. If the API fails or returns
    // unsuccessfully, leave the list empty and surface the error so the UI
    // can show a banner. Showing fake customers like "John Smith" was
    // masking real outages.
    const loadCustomers = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const data = await adminService.getCustomers();
            if (data.success && Array.isArray(data.customers)) {
                setCustomers(data.customers);
            } else {
                setCustomers([]);
                setError(data.error || 'Customer API returned no data');
            }
        } catch (err) {
            console.error('Error loading customers:', err);
            setCustomers([]);
            setError(err.message);
        } finally {
            setLoading(false);
        }
    }, []);

    // Initial Load
    useEffect(() => {
        loadCustomers();
    }, [loadCustomers]);

    // Update a single customer in the local state (avoid re-fetch)
    const updateLocalCustomer = (updatedCustomer) => {
        setCustomers(prev => prev.map(c =>
            c.UID === updatedCustomer.UID ? { ...c, ...updatedCustomer } : c
        ));
    };

    // Filter Logic
    useEffect(() => {
        let result = [...customers];

        // 1. Search
        if (searchQuery) {
            const q = searchQuery.toLowerCase();
            result = result.filter(c =>
                (c.fullName && c.fullName.toLowerCase().includes(q)) ||
                (c.email && c.email.toLowerCase().includes(q)) ||
                (c.accountNumber && c.accountNumber.toLowerCase().includes(q))
            );
        }

        // 2. Status
        if (statusFilter !== 'all') {
            result = result.filter(c => c.status === statusFilter);
        }

        // 2b. Provision status (B2B FIBRE Create)
        if (provisionFilter !== 'all') {
            result = result.filter(c => {
                const ps = c.provisionStatus || null;
                return provisionFilter === 'none' ? !ps : ps === provisionFilter;
            });
        }

        // 3. Sort
        result.sort((a, b) => {
            let aVal = a[sortConfig.key];
            let bVal = b[sortConfig.key];

            // Handle dates
            if (sortConfig.key === 'createdAt' || sortConfig.key === 'updatedAt') {
                aVal = new Date(aVal || 0).getTime();
                bVal = new Date(bVal || 0).getTime();
            }

            // Handle strings
            if (typeof aVal === 'string') aVal = aVal.toLowerCase();
            if (typeof bVal === 'string') bVal = bVal.toLowerCase();

            if (aVal < bVal) return sortConfig.direction === 'asc' ? -1 : 1;
            if (aVal > bVal) return sortConfig.direction === 'asc' ? 1 : -1;
            return 0;
        });

        setFilteredCustomers(result);
    }, [customers, searchQuery, statusFilter, provisionFilter, sortConfig]);

    return {
        customers,
        filteredCustomers,
        loading,
        error,
        loadCustomers,
        updateLocalCustomer,
        // Filter controls
        searchQuery,
        setSearchQuery,
        statusFilter,
        setStatusFilter,
        provisionFilter,
        setProvisionFilter,
        sortConfig,
        setSortConfig
    };
};
